import { computed, nextTick, onBeforeUnmount, ref, unref, watch } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const getDialogFocusableElements = root => {
  if (!root?.querySelectorAll) return [];

  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    element => element.getAttribute('aria-hidden') !== 'true'
  );
};

export const trapDialogTabFocus = ({ event, dialog, activeElement }) => {
  const elements = getDialogFocusableElements(dialog);
  if (!dialog || elements.length === 0) {
    event.preventDefault();
    dialog?.focus?.({ preventScroll: true });
    return true;
  }

  const first = elements[0];
  const last = elements[elements.length - 1];

  if (event.shiftKey && activeElement === first) {
    event.preventDefault();
    last.focus();
    return true;
  }

  if (!event.shiftKey && activeElement === last) {
    event.preventDefault();
    first.focus();
    return true;
  }

  return false;
};

// Ref-counted body scroll lock so nested/stacked dialogs don't prematurely
// restore scrolling while another overlay is still open.
let scrollLockCount = 0;
let previousHtmlOverflow = '';
const activeDialogStack = [];

const activateDialog = dialogId => {
  const existingIndex = activeDialogStack.indexOf(dialogId);
  if (existingIndex >= 0) activeDialogStack.splice(existingIndex, 1);
  activeDialogStack.push(dialogId);
};

const deactivateDialog = dialogId => {
  const existingIndex = activeDialogStack.indexOf(dialogId);
  if (existingIndex >= 0) activeDialogStack.splice(existingIndex, 1);
};

const isTopDialog = dialogId =>
  activeDialogStack[activeDialogStack.length - 1] === dialogId;

const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  if (scrollLockCount === 0) {
    previousHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  scrollLockCount += 1;
};

const unlockBodyScroll = () => {
  if (typeof document === 'undefined' || scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.documentElement.style.overflow = previousHtmlOverflow;
  }
};

export function useDialogA11y({
  isOpen,
  onClose,
  closeOnEscape = true,
  restoreFocus = true,
  lockScroll = true,
} = {}) {
  const dialogRef = ref(null);
  const opened = computed(() => Boolean(unref(isOpen)));
  let previousActiveElement = null;
  let scrollLocked = false;
  const dialogId = Symbol('dialog');

  const focusInitialElement = async () => {
    await nextTick();
    const dialog = dialogRef.value;
    if (!dialog) return;

    const [firstFocusable] = getDialogFocusableElements(dialog);
    (firstFocusable || dialog).focus?.({ preventScroll: true });
  };

  const handleKeydown = event => {
    if (!opened.value || !isTopDialog(dialogId)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (unref(closeOnEscape)) onClose?.();
      return;
    }

    if (event.key !== 'Tab') return;

    trapDialogTabFocus({
      event,
      dialog: dialogRef.value,
      activeElement: document.activeElement,
    });
  };

  const activate = () => {
    if (typeof document === 'undefined') return;

    previousActiveElement = document.activeElement;
    activateDialog(dialogId);
    document.addEventListener('keydown', handleKeydown);
    if (lockScroll && !scrollLocked) {
      lockBodyScroll();
      scrollLocked = true;
    }
    focusInitialElement();
  };

  const deactivate = () => {
    if (typeof document === 'undefined') return;

    document.removeEventListener('keydown', handleKeydown);
    deactivateDialog(dialogId);
    if (scrollLocked) {
      unlockBodyScroll();
      scrollLocked = false;
    }
    if (
      restoreFocus &&
      previousActiveElement &&
      typeof previousActiveElement.focus === 'function'
    ) {
      previousActiveElement.focus({ preventScroll: true });
    }
    previousActiveElement = null;
  };

  watch(opened, value => (value ? activate() : deactivate()), {
    flush: 'post',
    immediate: true,
  });

  onBeforeUnmount(deactivate);

  return { dialogRef };
}
